import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, User } from "lucide-react";
import { toast } from "sonner";
import { FEATURES } from "@/lib/features";

export default function AdminCustomerDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [notes, setNotes] = useState("");
  const [tagInput, setTagInput] = useState("");

  const load = () => api.get(`/admin/customers/${id}`).then((r) => { setData(r.data); setNotes(r.data.user.notes || ""); });
  useEffect(() => { load(); }, [id]);

  if (!data) return <div className="text-slate-400 font-mono text-xs uppercase tracking-widest">Loading…</div>;
  const u = data.user;

  const saveNotes = async () => {
    await api.patch(`/admin/customers/${id}`, { notes });
    toast.success("Notes saved");
  };
  const addTag = async () => {
    if (!tagInput.trim()) return;
    const tags = [...(u.tags || []), tagInput.trim()];
    await api.patch(`/admin/customers/${id}`, { tags });
    setTagInput(""); load();
  };
  const removeTag = async (t) => {
    const tags = (u.tags || []).filter((x) => x !== t);
    await api.patch(`/admin/customers/${id}`, { tags });
    load();
  };

  return (
    <div data-testid="admin-customer-detail">
      <Link to="/admin/customers" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"><ArrowLeft className="w-4 h-4" strokeWidth={1.75}/> Back to customers</Link>

      <div className="mt-4 border border-slate-200 rounded-md bg-white p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            {u.picture ? <img src={u.picture} alt="" className="w-14 h-14 rounded-md object-cover"/> : <div className="w-14 h-14 rounded-md bg-blue-600 text-white text-xl font-semibold flex items-center justify-center">{u.name?.[0]}</div>}
            <div>
              <h1 className="font-display text-2xl font-semibold text-slate-900">{u.name}</h1>
              <div className="text-sm text-slate-600">{u.email}</div>
              <div className="text-xs text-slate-400 mt-0.5">{u.email_verified ? "Email verified" : "Email not verified"} · Since {new Date(u.created_at).toLocaleDateString()}</div>
            </div>
          </div>
          <div className="text-right">
            <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Lifetime Value</div>
            <div className="font-display text-2xl font-semibold text-slate-900">${data.ltv.toFixed(2)}</div>
          </div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          {/* Orders */}
          <div className="border border-slate-200 rounded-md bg-white p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display text-lg font-semibold text-slate-900">Orders</h2>
              <span className="text-xs text-slate-500">{data.orders.length} total</span>
            </div>
            {data.orders.length === 0 ? <div className="text-sm text-slate-500">No orders.</div> : (
              <table className="w-full text-sm">
                <tbody>
                  {data.orders.map((o) => (
                    <tr key={o.order_id} className="border-b border-slate-100 last:border-b-0">
                      <td className="py-2 font-mono text-xs">{o.order_id}</td>
                      <td className="py-2 text-slate-600 text-xs">{new Date(o.placed_at).toLocaleDateString()}</td>
                      <td className="py-2"><Badge className="rounded-md border border-slate-200 bg-slate-50 text-slate-700 font-mono text-[10px] uppercase tracking-widest">{o.status}</Badge></td>
                      <td className="py-2 text-right font-mono">${Number(o.total).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Pets — hidden until FEATURES.pets is flipped back on */}
          {FEATURES.pets && (
            <div className="border border-slate-200 rounded-md bg-white p-5">
              <h2 className="font-display text-lg font-semibold text-slate-900 mb-3">Pets ({data.pets.length})</h2>
              {data.pets.length === 0 ? <div className="text-sm text-slate-500">No pets.</div> : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {data.pets.map((p) => (
                    <div key={p.pet_id} className="border border-slate-200 rounded-md p-3">
                      <div className="text-sm font-medium text-slate-900">{p.name}</div>
                      <div className="text-xs text-slate-500">{[p.species, p.breed].filter(Boolean).join(" · ")}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Tickets */}
          <div className="border border-slate-200 rounded-md bg-white p-5">
            <h2 className="font-display text-lg font-semibold text-slate-900 mb-3">Support history ({data.tickets.length})</h2>
            {data.tickets.length === 0 ? <div className="text-sm text-slate-500">No tickets.</div> : data.tickets.map((t) => (
              <Link key={t.ticket_id} to={`/admin/tickets/${t.ticket_id}`} className="block py-2 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 -mx-3 px-3">
                <div className="flex justify-between text-xs"><span className="font-mono">{t.ticket_id}</span><span className="text-slate-500">{t.status}</span></div>
                <div className="text-sm text-slate-900 mt-0.5">{t.subject}</div>
              </Link>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          <div className="border border-slate-200 rounded-md bg-white p-5">
            <div className="eyebrow mb-3">Onboarding</div>
            {u.onboarding_complete ? (
              <div className="text-sm text-blue-700 mb-3">✓ Completed{u.onboarding_completed_at && ` on ${new Date(u.onboarding_completed_at).toLocaleDateString()}`}</div>
            ) : (
              <div className="text-sm text-amber-700 mb-3">Pending — customer sees onboarding on next login</div>
            )}
            {u.account_type && <div className="text-xs text-slate-600 mb-2">Account type: <span className="text-slate-900">{u.account_type}</span></div>}
            {u.clinic_name && <div className="text-xs text-slate-600 mb-2">Clinic: <span className="text-slate-900">{u.clinic_name}</span></div>}
            {u.onboarding_complete && (
              <Button
                data-testid="reset-onboarding"
                onClick={async () => {
                  if (!window.confirm("Reset onboarding? The customer will need to complete it again on next login.")) return;
                  await api.post(`/admin/customers/${id}/reset-onboarding`);
                  toast.success("Onboarding reset");
                  load();
                }}
                variant="outline"
                size="sm"
                className="border-slate-300 rounded-md"
              >Reset onboarding</Button>
            )}
          </div>

          {/* Tags */}
          <div className="border border-slate-200 rounded-md bg-white p-5">
            <div className="eyebrow mb-3">Internal Tags</div>
            <div className="flex flex-wrap gap-1.5">
              {(u.tags || []).map((t) => (
                <button key={t} onClick={() => removeTag(t)} className="text-xs px-2.5 py-1 rounded-md bg-slate-100 hover:bg-red-50 text-slate-700 hover:text-red-700">{t} ×</button>
              ))}
            </div>
            <div className="flex gap-2 mt-3">
              <Input value={tagInput} onChange={(e) => setTagInput(e.target.value)} placeholder="Add tag" className="h-9 text-xs"/>
              <Button onClick={addTag} size="sm" className="bg-slate-900 hover:bg-blue-600 text-white">Add</Button>
            </div>
          </div>

          {/* Notes */}
          <div className="border border-slate-200 rounded-md bg-white p-5">
            <div className="eyebrow mb-3">Internal Notes</div>
            <Textarea rows={6} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Internal notes about this customer…"/>
            <Button onClick={saveNotes} className="mt-2 bg-blue-600 hover:bg-blue-700 text-white" size="sm">Save notes</Button>
          </div>

          {/* Marketing */}
          <div className="border border-slate-200 rounded-md bg-white p-5">
            <div className="eyebrow mb-3">Marketing</div>
            <div className="text-sm text-slate-700">Newsletter: {u.marketing_opt_in ? <span className="text-blue-700">Opted in</span> : <span className="text-slate-500">Opted out</span>}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
